import React from "react";
import BusinessIcon from "@mui/icons-material/Business";
import UploadIcon from "@mui/icons-material/Upload";
import Select from "react-select";
import { CountryDetails } from "src/store/services/partnerApi";
import states from "src/service/states";
import { GooglePlacesAutocomplete } from "src/components/inputs";

type FormData = {
  businessName: string;
  email: string;
  phoneNumber: string;
  phoneCountryCode: string;
  yearsOfOperation: string;
  physicalAddress: string;
  city: string;
  stateProvince: string;
  zipPostalCode: string;
  country: string;
  businessHoursOpen: string;
  businessHoursClose: string;
  isRegistered: string;
  rcNumber: string;
  isOwner: string;
  designation: string;
  businessBio: string;
  businessAccountManagerFirstName: string;
  businessAccountManagerLastName: string;
  businessAccountManagerPhoneNumber: string;
  businessAccountManagerPhoneCountryCode: string;
  businessAccountManagerGender: string;
  businessAccountManagerDesignation: string;
  accountManagerSourceBy: string;
  accountManagerPartnershipManager: string;
  businessAccountOfficerPhoneNumber: string;
  businessAccountOfficerPhoneCountryCode: string;
  can_deliver: string;
};
type OpeFormData = {
  operational_hours: {
    monday: { is_open: boolean; open: string; close: string };
    tuesday: { is_open: boolean; open: string; close: string };
    wednesday: { is_open: boolean; open: string; close: string };
    thursday: { is_open: boolean; open: string; close: string };
    friday: { is_open: boolean; open: string; close: string };
    saturday: { is_open: boolean; open: string; close: string };
    sunday: { is_open: boolean; open: string; close: string };
  };
};

type PartnerBasicInfoFormProps = {
  formData: FormData;
  opeFormData: OpeFormData;
  onInputChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => void;
  onLogoUpload?: (file: File) => void;
  logo?: File | null;
  logoUrl?: string;
  previewUrls?: any;
  onCoverUpload?: (file: File) => void;
  cover?: File | null;
  handleFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  showCover: boolean;
  handleOperationalHours: (
    day:
      | "monday"
      | "tuesday"
      | "wednesday"
      | "thursday"
      | "friday"
      | "saturday"
      | "sunday",
    field: "is_open" | "open" | "close",
    value: string | boolean,
  ) => void;
  coverUrl?: string;
  countryDetails?: CountryDetails[];
  onPlaceSelect?: (place: google.maps.places.PlaceResult) => void;
};

// Phone number formatter utility
const formatPhoneNumber = (value: string): string => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, "");

  // Format as XXX XXX XXXX (10 digits max)
  if (digits.length <= 3) {
    return digits;
  } else if (digits.length <= 6) {
    return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  } else {
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
  }
};

const PartnerBasicInfoForm: React.FC<PartnerBasicInfoFormProps> = ({
  formData,
  onInputChange,
  onLogoUpload,
  handleOperationalHours,
  opeFormData,
  logo,
  logoUrl,
  onCoverUpload,
  handleFileSelect,
  cover,
  previewUrls,
  coverUrl,
  showCover,
  countryDetails = [],
  onPlaceSelect,
}) => {
  const [logoPreview, setLogoPreview] = React.useState<string | null>(null);
  const [coverPreview, setCoverPreview] = React.useState<string | null>(null);
  const [bioCharCount, setBioCharCount] = React.useState<number>(0);

  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ] as const;

  React.useEffect(() => {
    if (logo) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(logo);
    } else if (logoUrl) {
      setLogoPreview(logoUrl);
    } else {
      setLogoPreview(null);
    }
  }, [logo, logoUrl]);

  React.useEffect(() => {
    if (cover) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPreview(reader.result as string);
      };
      reader.readAsDataURL(cover);
    } else if (coverUrl) {
      setCoverPreview(coverUrl);
    } else {
      setCoverPreview(null);
    }
  }, [cover, coverUrl]);

  // Update bio character count
  React.useEffect(() => {
    setBioCharCount(formData.businessBio.length);
  }, [formData.businessBio]);

  // Handle RC number input (alphanumeric, uppercase)
  const handleRcNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow only alphanumeric characters (letters and numbers), convert to uppercase
    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

    const event = {
      target: {
        name: "rcNumber",
        value,
      },
    } as React.ChangeEvent<HTMLInputElement>;
    onInputChange(event);
  };

  // Handle phone number formatting
  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    const event = {
      target: {
        name: e.target.name,
        value: formatted,
      },
    } as React.ChangeEvent<HTMLInputElement>;
    onInputChange(event);
  };

  // Handle bio input with character limit
  const handleBioChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= 420) {
      onInputChange(e);
    }
  };

  // Handle address change from Google Places Autocomplete
  const handleAddressChange = (address: string) => {
    const event = {
      target: {
        name: "physicalAddress",
        value: address,
      },
    } as React.ChangeEvent<HTMLInputElement>;
    onInputChange(event);
  };

  // Handle place selection and extract city, state, and zip code
  const handlePlaceSelect = (place: google.maps.places.PlaceResult) => {
    if (place.address_components) {
      let city = "";
      let state = "";
      let zipCode = "";

      // Extract city, state, and zip code from address components
      place.address_components.forEach((component) => {
        const types = component.types;

        // Get city (locality or administrative_area_level_2)
        if (
          types.includes("locality") ||
          types.includes("administrative_area_level_2")
        ) {
          if (!city) {
            city = component.long_name;
          }
        }

        // Get state (administrative_area_level_1)
        if (types.includes("administrative_area_level_1")) {
          state = component.long_name;
        }

        // Get zip code (postal_code)
        if (types.includes("postal_code")) {
          zipCode = component.long_name;
        }
      });

      // Update city if found
      if (city) {
        const cityEvent = {
          target: {
            name: "city",
            value: city,
          },
        } as React.ChangeEvent<HTMLInputElement>;
        onInputChange(cityEvent);
      }

      // Update state if found
      if (state) {
        // If country is Nigeria, try to match with Nigerian states list
        let stateValue = state;
        if (formData.country.toLowerCase() === "nigeria") {
          // Try to find exact match or partial match in Nigerian states
          const matchedState = states.find(
            (nigerianState) =>
              nigerianState.toLowerCase() === state.toLowerCase() ||
              state.toLowerCase().includes(nigerianState.toLowerCase()) ||
              nigerianState.toLowerCase().includes(state.toLowerCase()),
          );
          if (matchedState) {
            stateValue = matchedState;
          }
        }

        const stateEvent = {
          target: {
            name: "stateProvince",
            value: stateValue,
          },
        } as React.ChangeEvent<HTMLInputElement>;
        onInputChange(stateEvent);
      }
      

      // Update zip code if found
      if (zipCode) {
        const zipEvent = {
          target: {
            name: "zipPostalCode",
            value: zipCode,
          },
        } as React.ChangeEvent<HTMLInputElement>;
        onInputChange(zipEvent);
      }
    }

    // Call the external onPlaceSelect callback if provided
    if (onPlaceSelect) {
      onPlaceSelect(place);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0] && onLogoUpload) {
      onLogoUpload(e.target.files[0]);
    }
  };
  const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0] && onCoverUpload) {
      onCoverUpload(e.target.files[0]);
    }
  };

  const handleCountryChange = (selectedOption: any) => {
    if (selectedOption) {
      const selectedCountry = selectedOption.value as CountryDetails;

      // Update country field
      const countryEvent = {
        target: {
          name: "country",
          value: selectedCountry.name,
        },
      } as React.ChangeEvent<HTMLInputElement>;
      onInputChange(countryEvent);

      // Auto-populate dial code for business phone
      const phoneCodeEvent = {
        target: {
          name: "phoneCountryCode",
          value: selectedCountry.dialCode,
        },
      } as React.ChangeEvent<HTMLSelectElement>;
      onInputChange(phoneCodeEvent);

      // Auto-populate dial code for account manager phone
      const accountManagerCodeEvent = {
        target: {
          name: "businessAccountManagerPhoneCountryCode",
          value: selectedCountry.dialCode,
        },
      } as React.ChangeEvent<HTMLSelectElement>;
      onInputChange(accountManagerCodeEvent);
      // Auto-populate dial code for account manager phone
      const accountOfficerCodeEvent = {
        target: {
          name: "businessAccountOfficerPhoneCountryCode",
          value: selectedCountry.dialCode,
        },
      } as React.ChangeEvent<HTMLSelectElement>;
      onInputChange(accountOfficerCodeEvent);
    } else {
      // Clear country and dial codes if selection is cleared
      const countryEvent = {
        target: {
          name: "country",
          value: "",
        },
      } as React.ChangeEvent<HTMLInputElement>;
      onInputChange(countryEvent);

      const phoneCodeEvent = {
        target: {
          name: "phoneCountryCode",
          value: "",
        },
      } as React.ChangeEvent<HTMLSelectElement>;
      onInputChange(phoneCodeEvent);

      const accountManagerCodeEvent = {
        target: {
          name: "businessAccountManagerPhoneCountryCode",
          value: "",
        },
      } as React.ChangeEvent<HTMLSelectElement>;
      onInputChange(accountManagerCodeEvent);
    }
  };

  // Prepare options for react-select
  const countryOptions = countryDetails.map((country) => ({
    value: country,
    label: `${country.flag} ${country.name}`,
  }));

  // Find selected country option
  const selectedCountryOption = countryOptions.find(
    (option) => option.value.name === formData.country,
  );
  return (
    <div className="space-y-6">
      {/* Business Details */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-2 mb-6">
          <BusinessIcon className="text-gray-700" />
          <h2 className="text-lg font-semibold text-gray-900">
            Business Details
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Form fields remain the same as in the original component */}
          {/* Business Name */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Business Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="businessName"
              value={formData.businessName}
              onChange={onInputChange}
              placeholder="The Golden Spoon"
              required
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50"
            />
          </div>

          {/* Email Address */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={onInputChange}
              placeholder="contact@goldenspoon.com"
              required
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50"
            />
          </div>

          {/* Country */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Country <span className="text-red-500">*</span>
            </label>
            <Select
              options={countryOptions}
              value={selectedCountryOption}
              onChange={handleCountryChange}
              placeholder="Search and select country..."
              isSearchable
              isClearable
              className="react-select-container"
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: "48px",
                  borderColor: "#e5e7eb",
                  "&:hover": {
                    borderColor: "#e5e7eb",
                  },
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isSelected
                    ? "#7c2ebd"
                    : state.isFocused
                      ? "#f3f4f6"
                      : "white",
                  color: state.isSelected ? "white" : "#1f2937",
                }),
              }}
            />
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                name="phoneCountryCode"
                value={formData.phoneCountryCode || ""}
                readOnly
                placeholder="Dial code"
                className="px-3 py-3 border border-gray-200 rounded-lg bg-gray-100 text-gray-600 w-20 text-center cursor-not-allowed"
              />
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handlePhoneNumberChange}
                placeholder="901 234 7890"
                required
                maxLength={12}
                className="flex-1 px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50"
              />
            </div>
          </div>

          {/* Years of Operation */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Years of Operation
            </label>
            <select
              name="yearsOfOperation"
              value={formData.yearsOfOperation}
              onChange={onInputChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent outline-none bg-gray-50 appearance-none"
            >
              <option value="">Select Years of Operation</option>
              <option value="1">1 year</option>
              <option value="2">2 years</option>
              <option value="3">3 years</option>
              <option value="5">5 years</option>
              <option value="10">10+ years</option>
            </select>
          </div>

          {/* Physical Business Address */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Physical Business Address
            </label>
            <GooglePlacesAutocomplete
              value={formData.physicalAddress}
              onChange={handleAddressChange}
              onPlaceSelect={handlePlaceSelect}
              placeholder="123 Main Street, Ikeja"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50"
            />
          </div>

          {/* City */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              City
            </label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={onInputChange}
              placeholder="Ikeja"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50"
            />
          </div>

          {/* State/Province */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              State/Province
            </label>
            {formData.country.toLowerCase() === "nigeria" ? (
              <select
                name="stateProvince"
                value={formData.stateProvince}
                onChange={onInputChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent outline-none bg-gray-50 appearance-none"
              >
                <option value="">Select State</option>
                {states.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                name="stateProvince"
                value={formData.stateProvince}
                onChange={onInputChange}
                placeholder="Enter State/Province"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent outline-none bg-gray-50"
              />
            )}
          </div>

          {/* ZIP/Postal Code - Optional */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              ZIP/Postal Code{" "}
              <span className="text-gray-500 text-xs">(Optional)</span>
            </label>
            <input
              type="text"
              name="zipPostalCode"
              value={formData.zipPostalCode}
              onChange={onInputChange}
              placeholder="10011"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50"
            />
          </div>

          {/* Business Hours Open */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Business Hours (Open)
            </label>
            <input
              name="businessHoursOpen"
              value={formData.businessHoursOpen}
              onChange={onInputChange}
              type="time"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50 appearance-none"
            />
          </div>

          {/* Business Hours Close */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Business Hours (Close)
            </label>
            <input
              name="businessHoursClose"
              value={formData.businessHoursClose}
              onChange={onInputChange}
              type="time"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50 appearance-none"
            />
          </div>


          {/* Is your Business Registered? */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Is your Business Registered?
            </label>
            <select
              name="isRegistered"
              value={formData.isRegistered}
              onChange={onInputChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50 appearance-none"
            >
              <option value="">Select</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          {/* RC Number - Optional */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              RC Number{" "}
              <span className="text-gray-500 text-xs">(Optional)</span>
            </label>
            <input
              type="text"
              name="rcNumber"
              value={formData.rcNumber}
              onChange={handleRcNumberChange}
              placeholder="1234563"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Offer Delivery
            </label>
            
            <select
              name="can_deliver"
              value={formData.can_deliver}
              onChange={onInputChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50 appearance-none"
            >
              <option value="">Select</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          {/* Are you the Owner of the Business? */}

          {/* If No, Enter Designation */}
          {/* <div>
						<label className='block text-sm font-medium text-gray-900 mb-2'>
							If No, Enter Designation
						</label>
						<input
							type='text'
							name='designation'
							value={formData.designation}
							onChange={onInputChange}
							placeholder='HR Admin'
							className='w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50'
						/>
					</div> */}
        </div>
      </div>

      
      <div 
            className={`bg-white rounded-lg shadow-sm p-6 ${showCover ? "" : "hidden"} bg-white rounded-lg shadow-sm p-6`}>
          <div className="">
            <label className="block text-sm font-medium text-gray-900">
              Business Operational Hours
            </label>

            {days.map((day) => {
              const currentDay = opeFormData.operational_hours[day];

              return (
                <div
                  key={day}
                  className="grid grid-cols-1 md:grid-cols-4 gap-3 items-center border border-gray-200 rounded-lg p-4"
                >
                  {/* Day */}
                  <div className="font-medium capitalize text-gray-700">
                    {day}
                  </div>

                  {/* Open Toggle */}
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={currentDay.is_open}
                      onChange={(e) =>
                        handleOperationalHours(day, "is_open", e.target.checked)
                      }
                    />
                    <span className="text-sm text-gray-600">Open</span>
                  </label>

                  {/* Open Time */}
                  <input
                    type="time"
                    value={currentDay.open}
                    disabled={!currentDay.is_open}
                    onChange={(e) =>
                      handleOperationalHours(day, "open", e.target.value)
                    }
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50"
                  />

                  {/* Close Time */}
                  <input
                    type="time"
                    value={currentDay.close}
                    disabled={!currentDay.is_open}
                    onChange={(e) =>
                      handleOperationalHours(day, "close", e.target.value)
                    }
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50"
                  />
                </div>
              );
            })}
          </div>
          </div>

      {/* Business Bio */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-gray-900">
            Business Bio (Who We Are)
          </h2>
          <span
            className={`text-sm ${
              bioCharCount > 420 ? "text-red-500" : "text-gray-500"
            }`}
          >
            {bioCharCount}/420
          </span>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Description and additional information about the business
        </p>
        <textarea
          name="businessBio"
          value={formData.businessBio}
          onChange={handleBioChange}
          placeholder="Enter bio (max 420 characters)"
          rows={5}
          maxLength={420}
          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent outline-none bg-gray-50 resize-none"
        />
      </div>

      {/* Account Manager Sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Partner Contact Person */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Partner Contact Person
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            Additional information about the Account Manager
          </p>

          <div className="space-y-6">
            {/* Full Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  name="businessAccountManagerFirstName"
                  value={formData.businessAccountManagerFirstName}
                  onChange={onInputChange}
                  placeholder="Praise"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  name="businessAccountManagerLastName"
                  value={formData.businessAccountManagerLastName}
                  onChange={onInputChange}
                  placeholder="Ademide"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50"
                />
              </div>
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Phone Number
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="businessAccountManagerPhoneCountryCode"
                  value={formData.businessAccountManagerPhoneCountryCode || ""}
                  readOnly
                  placeholder="Dial code"
                  className="px-3 py-3 border border-gray-200 rounded-lg bg-gray-100 text-gray-600 w-20 text-center cursor-not-allowed"
                />
                <input
                  type="tel"
                  name="businessAccountManagerPhoneNumber"
                  value={formData.businessAccountManagerPhoneNumber}
                  onChange={handlePhoneNumberChange}
                  placeholder="901 234 7890"
                  maxLength={12}
                  className="flex-1 px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50"
                />
              </div>
            </div>

            {/* Gender */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Gender
              </label>
              <select
                name="businessAccountManagerGender"
                value={formData.businessAccountManagerGender}
                onChange={onInputChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50 appearance-none"
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Are you the Owner of the Business?
              </label>
              <select
                name="isOwner"
                value={formData.isOwner}
                onChange={onInputChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50 appearance-none"
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>

            {/* Designation */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Enter Designation
              </label>
              <input
                type="text"
                name="businessAccountManagerDesignation"
                value={formData.businessAccountManagerDesignation}
                onChange={onInputChange}
                placeholder="HR Admin"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50"
              />
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Business Logo
            </h2>
            <div className="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 h-48 flex items-center justify-center">
              {logoPreview ? (
                <div className="flex flex-col items-center gap-2">
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="h-32 w-32 object-contain rounded"
                  />
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLogoChange}
                    />
                    <div className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/80 transition-colors">
                      <UploadIcon fontSize="small" />
                      <span>Change Logo</span>
                    </div>
                  </label>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleLogoChange}
                  />
                  <div className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/80 transition-colors">
                    <UploadIcon fontSize="small" />
                    <span>Add Logo</span>
                  </div>
                </label>
              )}
            </div>
          </div>
          <div
            className={`bg-white rounded-lg shadow-sm p-6 ${showCover ? "" : "hidden"}`}
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Business Cover Image
            </h2>
            <div className="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 h-48 flex items-center justify-center">
              {coverPreview ? (
                <div className="flex flex-col items-center gap-2">
                  <img
                    src={coverPreview}
                    alt="cover preview"
                    className="h-32 w-32 object-contain rounded"
                  />
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      multiple
                      onChange={(e) => {
                        handleFileSelect(e);
                        handleCoverChange(e);
                      }}
                    />
                    <div className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/80 transition-colors">
                      <UploadIcon fontSize="small" />
                      <span>Change Cover Image</span>
                    </div>
                  </label>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    // onChange={handleCoverChange}
                    onChange={(e) => {
                      handleFileSelect(e);
                      handleCoverChange(e);
                    }}
                  />
                  <div className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/80 transition-colors">
                    <UploadIcon fontSize="small" />
                    <span>Add Cover Image</span>
                  </div>
                </label>
              )}
            </div>
          </div>
          {/* Account Manager (In-house) */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Account Manager (In-house)
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Additional information about the Assigned Admin
            </p>

            <div className="space-y-6">
              {/* Source by */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Sourced by (Full Name)
                </label>
                <input
                  type="text"
                  name="accountManagerSourceBy"
                  value={formData.accountManagerSourceBy}
                  onChange={onInputChange}
                  placeholder="Okunola Subomi"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50"
                />
              </div>

              {/* Partnership Manager */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Account Officer (Full Name)
                </label>
                <input
                  type="text"
                  name="accountManagerPartnershipManager"
                  value={formData.accountManagerPartnershipManager}
                  onChange={onInputChange}
                  placeholder="Okunola Subomi"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Phone Number
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="businessAccountOfficerPhoneCountryCode"
                    value={
                      formData.businessAccountOfficerPhoneCountryCode || ""
                    }
                    readOnly
                    placeholder="Dial code"
                    className="px-3 py-3 border border-gray-200 rounded-lg bg-gray-100 text-gray-600 w-20 text-center cursor-not-allowed"
                  />
                  <input
                    type="tel"
                    name="businessAccountOfficerPhoneNumber"
                    value={formData.businessAccountOfficerPhoneNumber}
                    onChange={handlePhoneNumberChange}
                    placeholder="901 234 7890"
                    maxLength={12}
                    className="flex-1 px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-gray-50"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerBasicInfoForm;
